import { useOutletContext, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { formatEUR } from "@/lib/api";

export default function ResellerNewCustomer() {
  const me = useOutletContext();
  const api = useApi();
  const navigate = useNavigate();
  const { data: products = [] } = useQuery({
    queryKey: ["reseller-products"],
    queryFn: async () => (await api.get("/reseller/products")).data,
  });

  const [productId, setProductId] = useState("");
  const [modules, setModules] = useState([]);
  const [anag, setAnag] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [fisc, setFisc] = useState({ ragione_sociale: "", piva: "", codice_fiscale: "", sdi_pec: "", sede: "" });

  const product = products.find((p) => p.id === productId);
  const total = product ? (product.price || 0) + (product.modules || []).filter((m) => modules.includes(m.id)).reduce((s, m) => s + (m.price || 0), 0) : 0;
  const readOnly = me?.read_only || me?.blocked;

  const create = useMutation({
    mutationFn: async () => (await api.post("/reseller/customers", {
      product_id: productId, module_ids: modules, anagrafica: anag, dati_fiscali: fisc,
    })).data,
    onSuccess: async (c) => {
      toast.success("Cliente creato — modulo d'ordine generato");
      if (c.order_pdf_id) {
        const r = await api.get(`/files/${c.order_pdf_id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a"); a.href = url; a.download = `modulo-ordine-${c.id.slice(0,8)}.pdf`; a.click();
      }
      navigate("/portal/customers");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (readOnly) {
    return <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-900 rounded-r-lg">Accesso in sola lettura: non può creare nuovi clienti finché la documentazione non viene approvata.</div>;
  }

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Nuovo cliente</h1>
        <p className="text-sm text-slate-500 mt-1">Al salvataggio viene generato il modulo d'ordine da far firmare al cliente, da ricaricare nel portale.</p>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 space-y-4">
        <div className="font-semibold">Prodotto e moduli</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div>
            <Label>Prodotto</Label>
            <Select value={productId} onValueChange={(v) => { setProductId(v); setModules([]); }}>
              <SelectTrigger data-testid="nc-product"><SelectValue placeholder="Scelga un prodotto" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Moduli</Label>
            <div className="space-y-1 mt-1">
              {(product?.modules || []).map((m) => (
                <label key={m.id} className="flex items-start gap-2 text-sm" data-testid={`nc-mod-${m.id}`}>
                  <Checkbox checked={modules.includes(m.id)} onCheckedChange={(v) => setModules(v ? [...modules, m.id] : modules.filter((x) => x !== m.id))} />
                  <div className="flex-1"><div className="font-medium">{m.name}</div><div className="text-xs text-slate-500">{m.description}</div></div>
                  {m.price > 0 && <div className="text-xs font-mono text-slate-600">+{formatEUR(m.price)}</div>}
                </label>
              ))}
              {!product && <div className="text-sm text-slate-500">Seleziona un prodotto per vedere i moduli.</div>}
            </div>
          </div>
        </div>
        {product && (
          <div className="flex items-center justify-between border-t border-[#E2DDD5] pt-3 text-sm" data-testid="nc-total">
            <span className="text-slate-500">Canone base {formatEUR(product.price || 0)} + moduli</span>
            <span className="font-semibold text-base">Totale annuo {formatEUR(total)}</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 space-y-4">
        <div className="font-semibold">Dati di contatto</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nome</Label><Input value={anag.nome} onChange={(e) => setAnag({ ...anag, nome: e.target.value })} data-testid="nc-nome" /></div>
          <div><Label>Cognome</Label><Input value={anag.cognome} onChange={(e) => setAnag({ ...anag, cognome: e.target.value })} data-testid="nc-cognome" /></div>
          <div><Label>Email</Label><Input value={anag.email} onChange={(e) => setAnag({ ...anag, email: e.target.value })} data-testid="nc-email" /></div>
          <div><Label>Telefono</Label><Input value={anag.telefono} onChange={(e) => setAnag({ ...anag, telefono: e.target.value })} data-testid="nc-tel" /></div>
        </div>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 space-y-4">
        <div className="font-semibold">Dati fiscali</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Ragione sociale / Nominativo</Label><Input value={fisc.ragione_sociale} onChange={(e) => setFisc({ ...fisc, ragione_sociale: e.target.value })} data-testid="nc-rag" /></div>
          <div><Label>P. IVA</Label><Input value={fisc.piva} onChange={(e) => setFisc({ ...fisc, piva: e.target.value })} data-testid="nc-piva" /></div>
          <div><Label>Codice Fiscale</Label><Input value={fisc.codice_fiscale} onChange={(e) => setFisc({ ...fisc, codice_fiscale: e.target.value })} data-testid="nc-cf" /></div>
          <div><Label>Codice SDI / PEC</Label><Input value={fisc.sdi_pec} onChange={(e) => setFisc({ ...fisc, sdi_pec: e.target.value })} data-testid="nc-sdi" /></div>
          <div><Label>Sede</Label><Input value={fisc.sede} onChange={(e) => setFisc({ ...fisc, sede: e.target.value })} data-testid="nc-sede" /></div>
        </div>
      </div>

      <Button onClick={() => create.mutate()} disabled={!productId || !anag.email} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-save-customer">Crea cliente e scarica modulo d'ordine</Button>
    </div>
  );
}
